import { useState, useEffect, createContext, useContext, createElement } from 'react'
import authService from '../services/authService'
import api from '../services/api'
import i18n from '../i18n.js'

// Create context
const AuthContext = createContext()

// User roles
export const userRoles = {
  ADMIN: 'Admin',
  Funder: 'Funder',
  PROJECT_MANAGER: 'Project Manager',
  FINANCE: 'Finance',
  CONSULTANT: 'Consultant'
}

// AuthProvider component
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Merge fresh /users/me fields (incl. v2 `capabilities`) into the stored user, so
  // feature-gating (delegated capabilities) works even though the login token response
  // doesn't carry them. Best-effort — keeps the existing user on any failure.
  const refreshUser = async () => {
    try {
      const r = await api.get('/users/me/')
      const stored = authService.getCurrentUser() || {}
      const merged = { ...stored, ...r.data, capabilities: r.data?.capabilities || [] }
      localStorage.setItem('user', JSON.stringify(merged))
      setUser(merged)
      // Their SAVED language wins over whatever the browser guessed on the login page. Applied
      // here because this runs both on a fresh load and straight after signing in, so somebody
      // who chose Arabic on their laptop gets Arabic on a borrowed machine too. Guarded on a
      // real change: changeLanguage always re-renders the tree, even when nothing differs.
      if (merged.language && merged.language !== i18n.resolvedLanguage) {
        i18n.changeLanguage(merged.language)
      }
    } catch { /* keep existing user */ }
  }

  useEffect(() => {
    const checkAuth = () => {
      let user = authService.getCurrentUser();
      // A login that has already expired goes straight to sign-in. Kept, it drew yesterday's dashboard,
      // every request was refused, and the screen sat empty until the person signed in again.
      try {
        const exp = JSON.parse(atob(user.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp
        if (exp && exp * 1000 < Date.now() + 30_000) { authService.logout(); user = null }
      } catch { /* no token or not readable: leave it to the server */ }

      if (user) {
        setIsAuthenticated(true)
        setUser(user)
        refreshUser()   // pull capabilities + latest profile in the background
      } else {
        setIsAuthenticated(false)
        setUser(null)
      }
      setLoading(false)
    }

    checkAuth()
    // Every tab shares one login. When another tab signs in, out, or as somebody else, this one
    // follows at once -- otherwise it kept showing the old person's screen with nobody's data.
    const onStorage = (e) => {
      if (e.key !== 'user' && e.key !== null) return
      // Only a different login counts. Every tab re-saves the profile as it loads; reloading on that
      // would have two tabs reload each other for ever.
      const who = (v) => { try { const u = JSON.parse(v || 'null'); return u ? `${u.id}|${u.access_token}` : '' } catch { return '' } }
      if (e.key === 'user' && who(e.oldValue) === who(e.newValue)) return
      window.location.reload()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);
      // Login returns access_token, but we need user details.
      // authService.login sets 'user' in localStorage with the response token?
      // Wait, let's verify authService.login. 
      // It sets 'user' to the WHOLE response data.
      // Does the login response include user details?
      // Backend: /auth/token returns { access_token, token_type, role, user_id, full_name, email }
      // Yes, usually I added that in previous turns or it's standard.
      // Let's assume it does. If not, we might need a /users/me call.

      // Let's decode or fetch user if needed. 
      // Based on my memory of similar implementations, the token endpoint often just returns token.
      // But let's check authService again. It saves the response.

      const currentUser = authService.getCurrentUser();
      setIsAuthenticated(true);
      setUser(currentUser);
      refreshUser();   // enrich with capabilities + profile right after login
      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      // The server sometimes answers with more than a sentence — e.g. "this account is managed
      // by another product, sign in there". Flattening every failure to "Login failed" threw
      // that away and left the person with no idea what to do next.
      if (error && typeof error === 'object' && error.code) {
        return { success: false, error: error.message || 'Login failed', detail: error };
      }
      return { success: false, error: typeof error === 'string' ? error : 'Login failed' };
    }
  }

  const register = async (userData) => {
    try {
      await authService.register(userData);
      // Auto login after register? Or just return success.
      return { success: true };
    } catch (error) {
      return { success: false, error: typeof error === 'string' ? error : 'Registration failed' };
    }
  }

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false)
    setUser(null)
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    register
  }

  return createElement(AuthContext.Provider, { value }, children)
}

// useAuth hook
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
